import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowLeft, ChevronLeft, ChevronRight, MapPin, MessageCircle, Ruler } from "lucide-react";
import api, { imgSrc } from "@/lib/api";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";

const WHATSAPP_URL =
  "https://wa.me/917057970705?text=Hi%2C%20I%20found%20you%20on%20your%20website%20and%20would%20like%20to%20enquire%20about%20your%20services.";

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    api
      .get("/projects")
      .then((r) => {
        const p = r.data.find((x) => x.id === id);
        if (p) setProject(p);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true));
  }, [id]);

  const onSelect = useCallback(() => {
    if (emblaApi) setSelected(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.on("select", onSelect);
    onSelect();
  }, [emblaApi, onSelect]);

  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, project]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-6 px-6" data-testid="project-not-found">
        <p className="font-display text-3xl">Project not found</p>
        <Link to="/" className="font-accent text-xs tracking-[0.25em] uppercase text-[#C5A059] border border-[#C5A059]/40 px-6 py-3 hover:bg-[#C5A059] hover:text-black transition-colors" data-testid="not-found-home-link">
          Back to Home
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center" data-testid="project-loading">
        <div className="w-8 h-8 border-2 border-[#C5A059] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const slides = [project.image, ...(project.gallery || [])].filter(Boolean);

  return (
    <div className="bg-black text-white min-h-screen" data-testid="project-detail-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24">
        <Reveal>
          <Link to="/" className="inline-flex items-center gap-2 font-accent text-[10px] tracking-[0.25em] uppercase text-zinc-500 hover:text-[#C5A059] transition-colors" data-testid="detail-back-link">
            <ArrowLeft size={14} /> All Projects
          </Link>
          <div className="mt-6 flex flex-wrap items-end justify-between gap-6 border-b border-white/10 pb-10">
            <div>
              <p className="font-accent text-xs tracking-[0.3em] uppercase text-[#C5A059] mb-3">{project.config}</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="detail-title">
                {project.name}
              </h1>
              <p className="mt-3 flex items-center gap-2 text-zinc-400" data-testid="detail-location">
                <MapPin size={15} className="text-[#C5A059]" /> {project.location}
              </p>
            </div>
            <span className="font-accent text-[10px] tracking-[0.25em] uppercase bg-black/70 px-4 py-2 border border-[#C5A059]/40 text-[#C5A059]" data-testid="detail-status">
              {project.status === "ongoing" ? "Now Rising" : "Delivered"}
            </span>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative mt-12 border border-[#C5A059]/20 p-3 sm:p-4 spotlight-frame" data-testid="detail-gallery">
            <div className="overflow-hidden relative aspect-[16/10] sm:aspect-[16/8]" ref={emblaRef}>
              <div className="flex h-full">
                {slides.map((s, i) => (
                  <div key={i} className="flex-[0_0_100%] min-w-0 h-full">
                    <img src={imgSrc(s)} alt={`${project.name} — photo ${i + 1}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
            {slides.length > 1 && (
              <>
                <button aria-label="Previous photo" onClick={() => emblaApi?.scrollPrev()} className="absolute left-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-black/70 backdrop-blur border border-[#C5A059]/40 text-[#C5A059] flex items-center justify-center hover:bg-[#C5A059] hover:text-black transition-colors" data-testid="detail-gallery-prev">
                  <ChevronLeft size={18} />
                </button>
                <button aria-label="Next photo" onClick={() => emblaApi?.scrollNext()} className="absolute right-6 top-1/2 -translate-y-1/2 z-10 w-11 h-11 bg-black/70 backdrop-blur border border-[#C5A059]/40 text-[#C5A059] flex items-center justify-center hover:bg-[#C5A059] hover:text-black transition-colors" data-testid="detail-gallery-next">
                  <ChevronRight size={18} />
                </button>
                <span className="absolute bottom-6 right-6 z-10 font-accent text-[10px] tracking-[0.25em] bg-black/70 backdrop-blur px-3 py-1.5 border border-white/15 text-zinc-300" data-testid="detail-gallery-counter">
                  {selected + 1} / {slides.length}
                </span>
              </>
            )}
          </div>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-3">
            <Reveal>
              <h2 className="font-display text-3xl tracking-tight" data-testid="detail-about-title">About This Address</h2>
              <p className="mt-6 text-zinc-400 text-base leading-relaxed" data-testid="detail-description">{project.description}</p>
            </Reveal>
            {project.features?.length > 0 && (
              <Reveal delay={0.1}>
                <h3 className="font-display text-2xl tracking-tight mt-12" data-testid="detail-features-title">Highlights</h3>
                <ul className="mt-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
                  {project.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 text-zinc-300 border-b border-white/10 pb-4">
                      <span className="w-1.5 h-1.5 rotate-45 bg-[#C5A059] shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}
            {project.floor_plans?.length > 0 && (
              <Reveal delay={0.15}>
                <h3 className="font-display text-2xl tracking-tight mt-12" data-testid="detail-floorplans-title">Floor Plans</h3>
                <div className="mt-6 grid sm:grid-cols-2 gap-6">
                  {project.floor_plans.map((fp, i) => (
                    <a key={i} href={imgSrc(fp)} target="_blank" rel="noopener noreferrer" className="block border border-[#C5A059]/20 p-3 hover:border-[#C5A059]/60 transition-colors group" data-testid={`floor-plan-${i}`}>
                      <img src={imgSrc(fp)} alt={`${project.name} floor plan ${i + 1}`} loading="lazy" className="w-full aspect-[4/3] object-contain bg-white/5 group-hover:scale-[1.02] transition-transform duration-500" />
                      <p className="mt-3 flex items-center gap-2 font-accent text-[10px] tracking-[0.2em] uppercase text-zinc-500 group-hover:text-[#C5A059] transition-colors">
                        <Ruler size={12} /> Plan {String(i + 1).padStart(2, "0")} — tap to enlarge
                      </p>
                    </a>
                  ))}
                </div>
              </Reveal>
            )}
          </div>
          <div className="lg:col-span-2 lg:sticky lg:top-32">
            <Reveal delay={0.1}>
              {project.specs?.length > 0 && (
                <div className="border border-[#C5A059]/30 bg-[#0A0A0A] p-8" data-testid="detail-specs">
                  <p className="font-accent text-[10px] tracking-[0.3em] uppercase text-[#C5A059] mb-6">Specifications</p>
                  <ul className="space-y-4">
                    {project.specs.map((s, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-zinc-300 border-b border-white/10 pb-4 last:border-0 last:pb-0">
                        <span className="w-1.5 h-1.5 rotate-45 bg-[#C5A059] shrink-0 mt-1.5" /> {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="mt-8 border border-[#C5A059]/30 bg-[#0A0A0A] p-8">
                <p className="font-display text-2xl tracking-tight">Interested in {project.name}?</p>
                <p className="mt-3 text-sm text-zinc-400 leading-relaxed">Speak with our team in Dahod for pricing, availability and site visits.</p>
                <div className="mt-6 flex flex-col gap-3">
                  <Link to="/#contact" className="bg-[#C5A059] text-black text-center font-accent text-xs tracking-[0.25em] uppercase px-6 py-4 hover:bg-[#D4AF37] hover:-translate-y-0.5 transition-all duration-300" data-testid="detail-enquire-button">
                    Send Enquiry
                  </Link>
                  <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="border border-[#C5A059]/40 text-[#C5A059] text-center font-accent text-xs tracking-[0.25em] uppercase px-6 py-4 hover:bg-[#C5A059] hover:text-black transition-colors duration-300 flex items-center justify-center gap-2" data-testid="detail-whatsapp-button">
                    <MessageCircle size={14} /> WhatsApp Us
                  </a>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
